
import React, { useState } from 'react';
import { Upload, Palette, Layout, Check, ArrowLeft } from 'lucide-react';
import Logo from './Logo';

const InvoiceDesigner = ({ setPage }) => {
  const [logoPreview, setLogoPreview] = useState(null);
  const [selectedColor, setSelectedColor] = useState('blue');
  const [selectedLayout, setSelectedLayout] = useState('modern');

  const colorSchemes = {
    blue: { primary: '#3b82f6', secondary: '#dbeafe', name: 'Ocean Blue' },
    green: { primary: '#10b981', secondary: '#d1fae5', name: 'Fresh Green' },
    purple: { primary: '#8b5cf6', secondary: '#ede9fe', name: 'Royal Purple' },
    orange: { primary: '#f59e0b', secondary: '#fef3c7', name: 'Sunset Orange' },
    navy: { primary: '#1e3a8a', secondary: '#dbeafe', name: 'Navy Professional' }
  };

  const layouts = {
    classic: { name: 'Classic', desc: 'Traditional invoice layout' },
    modern: { name: 'Modern', desc: 'Clean and contemporary' },
    minimal: { name: 'Minimal', desc: 'Simple and elegant' }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const ColorOption = ({ colorKey, scheme }) => {
    const isSelected = selectedColor === colorKey;
    const [isHovered, setIsHovered] = useState(false);

    return (
      <div
        onClick={() => setSelectedColor(colorKey)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          padding: '20px',
          borderRadius: '16px',
          border: `2px solid ${isSelected ? scheme.primary : '#e2e8f0'}`,
          backgroundColor: isSelected ? `${scheme.primary}10` : 'white',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
          boxShadow: isHovered ? '0 12px 24px rgba(0,0,0,0.1)' : '0 2px 4px rgba(0,0,0,0.05)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            background: `linear-gradient(135deg, ${scheme.primary} 0%, ${scheme.secondary} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 4px 12px ${scheme.primary}40`
          }}>
            {isSelected && <Check size={24} style={{ color: 'white', strokeWidth: 3 }} />}
          </div>
          <div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', marginBottom: '2px' }}>
              {scheme.name}
            </div>
            <div style={{ fontSize: '13px', color: '#64748b' }}>
              {isSelected ? 'Selected' : 'Click to select'}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const LayoutOption = ({ layoutKey, layout }) => {
    const isSelected = selectedLayout === layoutKey;
    const [isHovered, setIsHovered] = useState(false);

    return (
      <div
        onClick={() => setSelectedLayout(layoutKey)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          padding: '24px',
          borderRadius: '16px',
          border: `2px solid ${isSelected ? '#1A6BB5' : '#e2e8f0'}`,
          backgroundColor: isSelected ? 'rgba(26, 107, 181, 0.05)' : 'white',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
          boxShadow: isHovered ? '0 12px 24px rgba(0,0,0,0.1)' : '0 2px 4px rgba(0,0,0,0.05)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            background: isSelected ? 'linear-gradient(135deg, #0D2B5E 0%, #1A6BB5 100%)' : '#f1f5f9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease'
          }}>
            <Layout size={24} style={{ color: isSelected ? 'white' : '#64748b' }} strokeWidth={2.5} />
          </div>
          <div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', marginBottom: '2px' }}>
              {layout.name}
            </div>
            <div style={{ fontSize: '13px', color: '#64748b' }}>
              {layout.desc}
            </div>
          </div>
        </div>
        {isSelected && (
          <div style={{
            marginTop: '12px',
            padding: '8px 12px',
            backgroundColor: 'rgba(26,107,181,0.1)',
            borderRadius: '8px',
            fontSize: '13px',
            fontWeight: '600',
            color: '#0D2B5E',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <Check size={14} strokeWidth={3} />
            Active Layout
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(to bottom, #f8fafc 0%, #e2e8f0 100%)' }}>
      {/* Premium Header */}
      <div style={{
        background: 'linear-gradient(135deg, #0D2B5E 0%, #1A6BB5 100%)',
        color: 'white',
        padding: '48px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: '-50%',
          right: '-10%',
          width: '600px',
          height: '600px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(167, 139, 250, 0.3) 0%, transparent 70%)',
          animation: 'pulse 4s ease-in-out infinite'
        }} />

        <div style={{ maxWidth: '1400px', margin: '0 auto', position: 'relative' }}>
          <button
            onClick={() => setPage('dashboard')}
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '12px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '32px',
              backdropFilter: 'blur(10px)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
          >
            <ArrowLeft size={18} />
            Back to Dashboard
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
            <div style={{
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '16px',
              padding: '12px',
              backdropFilter: 'blur(10px)'
            }}>
              <Palette size={32} strokeWidth={2.5} />
            </div>
            <div>
              <h1 style={{ fontSize: '42px', fontWeight: '900', marginBottom: '8px', letterSpacing: '-2px' }}>
                Invoice Designer
              </h1>
              <p style={{ fontSize: '18px', opacity: 0.95 }}>
                Customize your invoice appearance and branding
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '48px', marginTop: '-40px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
          
          {/* Left Column - Controls */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
            
            {/* Logo Upload */}
            <div style={{
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              borderRadius: '16px',
              padding: '32px',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
              border: '1px solid rgba(226, 232, 240, 0.8)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                <div style={{
                  background: 'linear-gradient(135deg, #0D2B5E 0%, #1A6BB5 100%)',
                  borderRadius: '12px',
                  padding: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Upload size={20} style={{ color: 'white' }} strokeWidth={2.5} />
                </div>
                <h3 style={{ fontSize: '20px', fontWeight: '800', color: '#0f172a' }}>Upload Logo</h3>
              </div>

              <div style={{
                border: '2px dashed #cbd5e1',
                borderRadius: '12px',
                padding: '32px',
                textAlign: 'center',
                backgroundColor: '#f8fafc',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = '#1A6BB5'}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = '#cbd5e1'}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  style={{ display: 'none' }}
                  id="logo-upload"
                />
                <label htmlFor="logo-upload" style={{ cursor: 'pointer' }}>
                  {logoPreview ? (
                    <img src={logoPreview} alt="Logo" style={{ maxWidth: '200px', maxHeight: '100px' }} />
                  ) : (
                    <div>
                      <Upload size={40} style={{ color: '#1A6BB5', margin: '0 auto 12px' }} />
                      <div style={{ fontSize: '15px', fontWeight: '600', color: '#0f172a', marginBottom: '4px' }}>
                        Click to upload logo
                      </div>
                      <div style={{ fontSize: '13px', color: '#64748b' }}>
                        PNG, JPG up to 5MB
                      </div>
                    </div>
                  )}
                </label>
              </div>
            </div>

            {/* Color Schemes */}
            <div style={{
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              borderRadius: '16px',
              padding: '32px',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
              border: '1px solid rgba(226, 232, 240, 0.8)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                <div style={{
                  background: 'linear-gradient(135deg, #0D2B5E 0%, #1A6BB5 100%)',
                  borderRadius: '12px',
                  padding: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Palette size={20} style={{ color: 'white' }} strokeWidth={2.5} />
                </div>
                <h3 style={{ fontSize: '20px', fontWeight: '800', color: '#0f172a' }}>Color Scheme</h3>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {Object.entries(colorSchemes).map(([key, scheme]) => (
                  <ColorOption key={key} colorKey={key} scheme={scheme} />
                ))}
              </div>
            </div>

            {/* Layout Style */}
            <div style={{
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              borderRadius: '16px',
              padding: '32px',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
              border: '1px solid rgba(226, 232, 240, 0.8)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                <div style={{
                  background: 'linear-gradient(135deg, #0D2B5E 0%, #1A6BB5 100%)',
                  borderRadius: '12px',
                  padding: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Layout size={20} style={{ color: 'white' }} strokeWidth={2.5} />
                </div>
                <h3 style={{ fontSize: '20px', fontWeight: '800', color: '#0f172a' }}>Layout Style</h3>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {Object.entries(layouts).map(([key, layout]) => (
                  <LayoutOption key={key} layoutKey={key} layout={layout} />
                ))}
              </div>
            </div>

            {/* Save Button */}
            <button
              onClick={() => alert('Invoice template saved!')}
              style={{
                background: 'linear-gradient(135deg, #6DC234 0%, #5AAD28 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '16px',
                padding: '20px',
                fontSize: '18px',
                fontWeight: '800',
                cursor: 'pointer',
                boxShadow: '0 8px 16px rgba(109,194,52,0.3)',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 12px 24px rgba(109,194,52,0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 16px rgba(109,194,52,0.3)';
              }}
            >
              <Check size={24} strokeWidth={3} />
              Save Template
            </button>
          </div>

          {/* Right Column - Preview */}
          <div style={{
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: '16px',
            padding: '40px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            border: '1px solid rgba(226, 232, 240, 0.8)',
            position: 'sticky',
            top: '32px',
            height: 'fit-content'
          }}>
            <h3 style={{ fontSize: '20px', fontWeight: '800', color: '#0f172a', marginBottom: '24px' }}>
              Live Preview
            </h3>
            
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '32px',
              border: `3px solid ${colorSchemes[selectedColor].primary}`,
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
            }}>
              {logoPreview && (
                <img src={logoPreview} alt="Logo" style={{ maxWidth: '150px', marginBottom: '24px' }} />
              )}
              
              <div style={{
                backgroundColor: colorSchemes[selectedColor].secondary,
                padding: '20px',
                borderRadius: '8px',
                marginBottom: '24px'
              }}>
                <div style={{ fontSize: '24px', fontWeight: '800', color: colorSchemes[selectedColor].primary }}>
                  INVOICE
                </div>
                <div style={{ fontSize: '14px', color: '#64748b', marginTop: '4px' }}>
                  #INV-2024-001
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>Bill To:</div>
                <div style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>Target Corporation</div>
              </div>

              <div style={{ 
                borderTop: '2px solid #e2e8f0', 
                paddingTop: '20px',
                marginTop: '20px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <span style={{ fontSize: '14px', color: '#64748b' }}>Item</span>
                  <span style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>$100.00</span>
                </div>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  paddingTop: '16px',
                  borderTop: '2px solid #e2e8f0'
                }}>
                  <span style={{ fontSize: '18px', fontWeight: '800', color: colorSchemes[selectedColor].primary }}>
                    Total
                  </span>
                  <span style={{ fontSize: '18px', fontWeight: '800', color: colorSchemes[selectedColor].primary }}>
                    $100.00
                  </span>
                </div>
              </div>

              <div style={{
                marginTop: '24px',
                padding: '12px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                fontSize: '12px',
                color: '#64748b',
                textAlign: 'center'
              }}>
                Layout: {layouts[selectedLayout].name} • Color: {colorSchemes[selectedColor].name}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.05); }
        }
      `}</style>
    </div>
  );
};

export default InvoiceDesigner;